// two bobby wishlist (11 Oct 2026): hearts on cards and product pages, a count in the header, and the Saved page.
// Saved handles live in localStorage "tb_wishlist" on this device. No sign-in, no sharing (founders, 11 Oct).
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

const KEY = 'tb_wishlist';
const read = () => { try { const v = JSON.parse(localStorage.getItem(KEY) || '[]'); return Array.isArray(v) ? v.filter((h) => typeof h === 'string') : []; } catch (e) { return []; } };
const write = (list) => { try { localStorage.setItem(KEY, JSON.stringify(list.slice(0, 60))); } catch (e) {} broadcast(); };
const has = (h) => read().includes(h);
const toggle = (h) => { const l = read(); const i = l.indexOf(h); if (i >= 0) l.splice(i, 1); else l.unshift(h); write(l); return i < 0; };
const broadcast = () => document.dispatchEvent(new CustomEvent('tb:wishlist', { detail: { handles: read() } }));
window.tbWishlist = { read, has, toggle };

// Meta "AddToWishlist" on every new heart (11 Oct 2026). content_ids = Shopify product id, which the Meta catalog holds as
// retailer_product_group_id, so Events Manager can report hearts per product and ads can build a "hearted, not bought" audience.
// No personal data is added here; the pixel itself is the Facebook & Instagram app's.
const track = (d) => {
  try {
    if (typeof window.fbq !== 'function' || !d.productId) return;
    const value = Number(d.price);
    window.fbq('track', 'AddToWishlist', {
      content_ids: [String(d.productId)], content_type: 'product_group', content_name: d.title || '',
      ...(Number.isFinite(value) && value > 0 ? { value, currency: 'INR' } : {}),
    });
  } catch (e) {}
};

class TbHeart extends HTMLElement {
  connectedCallback() {
    this.btn = this.querySelector('button');
    this.sync();
    this.btn?.addEventListener('click', this.onClick);
    document.addEventListener('tb:wishlist', this.sync);
  }
  disconnectedCallback() { document.removeEventListener('tb:wishlist', this.sync); }
  onClick = (e) => {
    e.preventDefault(); e.stopPropagation();
    const on = toggle(this.dataset.handle);
    if (on) track(this.dataset);
    this.classList.add('tb-heart--pop'); setTimeout(() => this.classList.remove('tb-heart--pop'), 350);
    const live = document.getElementById('tb-heart-live'); if (live) live.textContent = on ? 'Saved' : 'Removed from saved';
  };
  sync = () => {
    const on = has(this.dataset.handle);
    this.toggleAttribute('data-on', on);
    if (this.btn) {
      this.btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      const label = this.btn.querySelector('.tb-heart__label'); if (label) label.textContent = on ? 'Saved' : 'Save';
    }
  };
}

class TbHeartCount extends HTMLElement {
  connectedCallback() { this.sync(); document.addEventListener('tb:wishlist', this.sync); }
  disconnectedCallback() { document.removeEventListener('tb:wishlist', this.sync); }
  sync = () => { const n = read().length; this.textContent = n > 99 ? '99+' : String(n); this.hidden = n === 0; this.closest('.tb-head-heart')?.toggleAttribute('data-has', n > 0); };
}

const money = (paise) => '₹' + Math.round(paise / 100).toLocaleString('en-IN');
const cache = new Map();
const product = async (h) => { if (!cache.has(h)) cache.set(h, fetch(`/products/${h}.js`, { headers: { Accept: 'application/json' } }).then((r) => (r.ok ? r.json() : null)).catch(() => null)); return cache.get(h); };

class TbSavedList extends HTMLElement {
  connectedCallback() { this.grid = this.querySelector('[data-grid]'); this.render(); document.addEventListener('tb:wishlist', this.render); this.addEventListener('click', this.onClick); }
  disconnectedCallback() { document.removeEventListener('tb:wishlist', this.render); }
  render = async () => {
    const handles = read();
    const empty = document.querySelector(this.dataset.emptySelector || '.tb-saved__empty');
    if (empty) empty.hidden = handles.length > 0;
    if (!this.grid) return;
    if (!handles.length) { this.grid.innerHTML = ''; return; }
    const items = (await Promise.all(handles.map(product))).filter(Boolean);
    const gone = handles.filter((h) => !items.find((p) => p.handle === h));
    if (gone.length) { try { localStorage.setItem(KEY, JSON.stringify(handles.filter((h) => !gone.includes(h)))); } catch (e) {} }
    this.grid.innerHTML = items.map((p) => {
      const img = p.featured_image ? p.featured_image.replace(/(\.[a-z]+)(\?|$)/i, '_600x$1$2') : '';
      const v = p.variants.find((x) => x.available) || p.variants[0];
      const single = p.variants.length === 1;
      const price = money(p.price); const was = p.compare_at_price > p.price ? money(p.compare_at_price) : '';
      const cta = !p.available ? '<span class="tb-saved__soldout">Sold out</span>'
        : single ? `<button type="button" class="button tb-saved__add" data-variant="${v.id}" data-title="${p.title.replace(/"/g, '&quot;')}">Add to cart</button>`
        : `<a class="button button-secondary" href="/products/${p.handle}">Choose colour</a>`;
      return `<article class="tb-saved__card"${p.available ? '' : ' data-soldout'}>
        <a class="tb-saved__img" href="/products/${p.handle}">${img ? `<img src="${img}" alt="${p.title.replace(/"/g, '&quot;')}" loading="lazy" width="600" height="600">` : ''}</a>
        <tb-heart class="tb-heart tb-heart--card" data-handle="${p.handle}" data-product-id="${p.id}" data-price="${p.price / 100}" data-title="${p.title.replace(/"/g, '&quot;')}"><button type="button" class="tb-heart__btn button-unstyled" aria-pressed="true" aria-label="Remove ${p.title.replace(/"/g, '&quot;')} from saved"><svg class="tb-heart__svg" viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><path class="tb-heart__path" d="M12 20.5s-7.2-4.4-9.3-9C1.2 8 3.1 4.4 6.7 4.4c2 0 3.5 1.1 4.3 2.4.8-1.3 2.3-2.4 4.3-2.4 3.6 0 5.5 3.6 4 7.1-2.1 4.6-9.3 9-9.3 9z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/></svg></button></tb-heart>
        <div class="tb-saved__body"><a class="tb-saved__title" href="/products/${p.handle}">${p.title}</a><p class="tb-saved__price">${price}${was ? ` <s>${was}</s>` : ''}</p>${cta}</div>
      </article>`;
    }).join('');
  };
  onClick = async (e) => {
    const btn = e.target.closest('.tb-saved__add'); if (!btn) return;
    btn.setAttribute('aria-busy', 'true'); const label = btn.textContent; btn.textContent = 'Adding…';
    const sections = [...document.querySelectorAll('cart-items-component')].map((el) => el.dataset.sectionId).filter(Boolean).join(',');
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(new CartLinesUpdateEvent({ action: 'add', context: 'tb-saved', lines: [{ merchandiseId: String(btn.dataset.variant), quantity: 1 }], promise: deferred.promise }));
    try {
      const response = await fetch('/cart/add.js', { ...fetchConfig('json', { body: JSON.stringify({ items: [{ id: Number(btn.dataset.variant), quantity: 1 }], sections }) }) }).then((r) => r.json());
      if (response.status) throw Object.assign(new Error(response.description || response.message), { response });
      const ajaxCart = await fetch('/cart.js').then((r) => r.json());
      deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart), detail: { items: ajaxCart.items, source: 'tb-saved', itemCount: 1, sections: response.sections, didError: false } });
      btn.textContent = 'Added'; setTimeout(() => { btn.textContent = label; btn.removeAttribute('aria-busy'); }, 1500);
    } catch (error) {
      deferred.reject(error); this.dispatchEvent(new CartErrorEvent({ error: error?.message || 'Could not add to cart', code: 'INVALID' }));
      btn.textContent = label; btn.removeAttribute('aria-busy');
    }
  };
}

if (!customElements.get('tb-heart')) customElements.define('tb-heart', TbHeart);
if (!customElements.get('tb-heart-count')) customElements.define('tb-heart-count', TbHeartCount);
if (!customElements.get('tb-saved-list')) customElements.define('tb-saved-list', TbSavedList);
if (!document.getElementById('tb-heart-live')) { const s = document.createElement('span'); s.id = 'tb-heart-live'; s.className = 'visually-hidden'; s.setAttribute('role', 'status'); document.body.appendChild(s); }
